import { useState } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import { AddCompanyMemberAccountButton } from "components/AddCompanyMemberAccountButton";
import { CompanyBranches } from "components/CompanyBranches";
import { EvotruxDivider } from "components/EvotruxDivider";
import { ManageSubscriptionButton } from "components/ManageSubscriptionButton";
import { RefreshPageButton } from "components/RefreshPageButton";
import { EVOTRUX_BLUE } from "constants/index";
import { useRoleContext } from "hooks";
import { useSubscriptionStatus } from "hooks/useSubscriptionStatus";
import {
  getSubscriptionPlanLabel,
  ISOStringToReadableDate,
  isTrialShipperAwaitingPostingPermission,
  renderInfo,
} from "utils";

import { CompanyDetailRoles } from "../../CompanyDetailRoles";
import { ActiveJoinCompanyRequestTable } from "../ActiveJoinCompanyRequestTable";
import { BookedPriceRevealToggle } from "../BookedPriceRevealToggle";
import { EditCompanyAddressModal } from "../EditCompanyAddressModal";
import { ShipperPremiumLicenceSubscriptionDetail } from "../ShipperPremiumLicenceSubscriptionDetail";

interface Props {
  company: CompanyDetailInfo | null;
  onRefreshCompanyInfo: () => void;
}

export function AdminCompanyDetails(props: Props) {
  const { company, onRefreshCompanyInfo } = props;
  const { subscription } = useSubscriptionStatus();

  const { getAdminRole } = useRoleContext();

  const admin = getAdminRole();
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  if (admin == null) {
    return null;
  }

  const companyId = company?.company.id;
  const editableAddress = company?.company.address?.[0];

  return (
    <Stack direction="column">
      {company?.company.id && (
        <Stack
          direction="row"
          alignItems="stretch"
          spacing={1}
          pt="1rem"
          pb="1rem"
        >
          <ManageSubscriptionButton />
          <AddCompanyMemberAccountButton onSuccess={onRefreshCompanyInfo} />
        </Stack>
      )}
      <Typography variant="h4" mt=".5rem">
        Company Information
      </Typography>
      <EvotruxDivider />
      <Box mt="1rem" flex={1}>
        {subscription != null &&
        renderInfo(subscription.subscription_plan) &&
        !isTrialShipperAwaitingPostingPermission(subscription) ? (
          <Typography variant="h6">
            <b>Subscription Plan:</b>{" "}
            {getSubscriptionPlanLabel(subscription.subscription_plan)}
          </Typography>
        ) : null}
        {renderInfo(company?.company.description) ? (
          <Typography variant="h6" style={{ whiteSpace: "pre-line" }}>
            <b>Description:</b> {company?.company.description}
          </Typography>
        ) : null}
        {renderInfo(company?.company.mc_number) ? (
          <Typography variant="h6">
            <b>MC number:</b>
            {company?.company.mc_number}
          </Typography>
        ) : null}
        {editableAddress && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h6">
              <b>Address:</b> {editableAddress.address}, {editableAddress.city},{" "}
              {editableAddress.province}, {editableAddress.country}{" "}
            </Typography>
            <Tooltip title="Edit company address">
              <IconButton
                size="medium"
                aria-label="Edit company address"
                onClick={() => setAddressModalOpen(true)}
                sx={{ color: EVOTRUX_BLUE }}
              >
                <EditOutlinedIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Typography variant="h6" color="text.secondary">
          Member since:{" "}
          {ISOStringToReadableDate(company?.company.created_at ?? "")}
        </Typography>
      </Box>
      {companyId != null &&
        company?.company.company_type === "SHIPPER" &&
        company.preferences != null && (
          <Box mt="2rem" flex={1}>
            <Typography variant="h4">Booked price</Typography>
            <EvotruxDivider />
            <Box mt="1rem">
              <BookedPriceRevealToggle
                companyId={companyId}
                enabled={company.preferences.booked_price_reveal_enabled}
                onSuccess={onRefreshCompanyInfo}
              />
            </Box>
          </Box>
        )}
      {company != null && (
        <Box mt="2rem" flex={1}>
          <Stack direction="row">
            <Typography variant="h4">Join Company Requests</Typography>
            <RefreshPageButton
              id="refresh-join-requests"
              onRefresh={onRefreshCompanyInfo}
              iconButton
            />
          </Stack>
          <EvotruxDivider />
          <Box display="flex" mt="1rem" flexWrap="wrap">
            <ActiveJoinCompanyRequestTable
              company={company}
              onSuccess={onRefreshCompanyInfo}
            />
          </Box>
        </Box>
      )}
      {company?.company.company_type === "SHIPPER" && company.is_admin && (
        <Box mt="2rem" flex={1}>
          <Stack direction="row">
            <Typography variant="h4">Company Branches</Typography>
            <RefreshPageButton
              id="refresh-branches"
              onRefresh={onRefreshCompanyInfo}
              iconButton
            />
          </Stack>
          <EvotruxDivider />
          {company && (
            <CompanyBranches
              branches={company.branches}
              viewingAsRoles={[admin]}
              viewingAsCompanyAdmin
            />
          )}
        </Box>
      )}
      <Box mt="2rem" flex={1}>
        <Stack direction="row">
          <Typography variant="h4">Company Members</Typography>
          <RefreshPageButton
            id="refresh-branches"
            onRefresh={onRefreshCompanyInfo}
            iconButton
          />
        </Stack>
        <EvotruxDivider />
        <ShipperPremiumLicenceSubscriptionDetail />
        <Box mt="1rem" display="flex" flexWrap="wrap" gap="1rem">
          {company?.admins && company.admins.length > 0 && (
            <CompanyDetailRoles roles={company.admins} title="Administrators" />
          )}
          {company?.carriers && company.carriers.length > 0 && (
            <CompanyDetailRoles roles={company.carriers} title="Carriers" />
          )}
          {company?.shippers && company?.shippers.length > 0 && (
            <CompanyDetailRoles roles={company.shippers} title="Shippers" />
          )}
          {company?.branch_admins && company?.branch_admins.length > 0 && (
            <CompanyDetailRoles
              roles={company.branch_admins}
              title="Branch Administrators"
            />
          )}
        </Box>
      </Box>
      {companyId != null && editableAddress && addressModalOpen && (
        <EditCompanyAddressModal
          open
          onClose={() => setAddressModalOpen(false)}
          onSuccess={() => {
            setAddressModalOpen(false);
            onRefreshCompanyInfo();
          }}
          companyId={companyId}
          address={editableAddress}
        />
      )}
    </Stack>
  );
}
